import {
    type ApiError,
    type ApiGetNotifications,
    type ApiNotificationResourceType,
    type ApiNotificationUpdateParams,
    type ApiSuccessEmpty,
    type Notification,
} from '@lightdash/common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type LightdashApi } from '../../../api';
import { useLightdashApi } from '../../../providers/LightdashApi/useLightdashApi';

type NotificationForResource<T extends ApiNotificationResourceType> = Extract<
    Notification,
    { resourceType: T }
>;

const getNotifications = async <T extends ApiNotificationResourceType>(
    lightdashApi: LightdashApi,
    type: T,
): Promise<NotificationForResource<T>[]> => {
    const results = await lightdashApi<ApiGetNotifications['results']>({
        url: `/notifications?type=${type}`,
        method: 'GET',
        body: undefined,
    });

    return results as NotificationForResource<T>[];
};

export const useGetNotifications = <T extends ApiNotificationResourceType>(
    type: T,
    enabled: boolean,
) => {
    const lightdashApi = useLightdashApi();
    return useQuery<NotificationForResource<T>[], ApiError>(
        ['notifications', type],
        () => getNotifications(lightdashApi, type),
        {
            refetchInterval: 3 * 60 * 1000, // 3 minutes
            retry: (_, error) => error.error.statusCode !== 403,
            enabled,
        },
    );
};

const updateNotification = (
    lightdashApi: LightdashApi,
    notificationId: string,
    toUpdate: ApiNotificationUpdateParams,
) =>
    lightdashApi<ApiSuccessEmpty>({
        url: `/notifications/${notificationId}`,
        method: 'PATCH',
        body: JSON.stringify(toUpdate),
    });

export const useUpdateNotification = () => {
    const lightdashApi = useLightdashApi();
    const queryClient = useQueryClient();

    return useMutation<
        ApiSuccessEmpty,
        ApiError,
        { toUpdate: ApiNotificationUpdateParams } & Pick<
            Notification,
            'notificationId' | 'resourceType'
        >
    >(
        ({ notificationId, toUpdate }) =>
            updateNotification(lightdashApi, notificationId, toUpdate),
        {
            mutationKey: ['update-notification'],
            onSuccess: async (_, { resourceType }) => {
                await queryClient.invalidateQueries([
                    'notifications',
                    resourceType,
                ]);

                // TODO: Invalidate dashboard comments query if after viewing a dashboard comment notification and on the current dashboard
            },
        },
    );
};
